import axios from 'axios';
import alert_mixin from '@/utils/mixins/alert_mixin';
import {
  BIND_VERIFICATION_SERVICES,
  GET_VERIFICATION_SERVICES,
  GET_VERIFICATION_SERVICE_BINDINGS,
  REMOVE_VERIFICATION_BINDING,
  get_request_config,
} from '@/utils/system_constants/constant';

export default {
  name: 'BindServices',
  mixins: [alert_mixin],
  props: {
    entityTypeId: {
      type: Number,
      required: false,
      default: null,
    },
    entityTypeName: {
      type: String,
      required: false,
      default: '',
    },
    entityConfigId: {
      type: Number,
      required: false,
      default: null,
    },
  },
  data: () => ({
    services: [],
    service_bindings: [],
    selected_service_ids: [],
    creating_service_binding: false,
    removing_binding_id: null,
    service_dropdown_open: false,
    services_loading: false,
  }),
  computed: {
    available_services_count() {
      return Array.isArray(this.services) ? this.services.length : 0;
    },
    bound_services_count() {
      return Array.isArray(this.service_bindings) ? this.service_bindings.length : 0;
    },
    selected_services_count() {
      return Array.isArray(this.selected_service_ids) ? this.selected_service_ids.length : 0;
    },
    selected_services_label() {
      if (this.services_loading) return 'Loading services...';
      if (!this.bindable_services.length) return 'No services available';
      if (!this.selected_services_count) return 'Select service(s) to bind';
      return `${this.selected_services_count} service${this.selected_services_count === 1 ? '' : 's'} selected`;
    },
    bindable_services() {
      const bound_service_ids = new Set(
        (this.service_bindings || [])
          .map((binding) => Number(
            binding?.verification_service_id ??
            binding?.service_id ??
            binding?.service ??
            binding?.verification_service
          ))
          .filter((id) => Number.isFinite(id) && id > 0)
      );

      return (Array.isArray(this.services) ? this.services : [])
        .filter((service) => Number.isFinite(Number(service?.id)))
        .filter((service) => !bound_service_ids.has(Number(service.id)));
    },
  },
  mounted() {
    this.fetch_services();
    this.fetch_service_bindings();
    document.addEventListener('click', this.on_document_click, true);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.on_document_click, true);
  },

  methods: {
    on_document_click(event) {
      if (!this.service_dropdown_open) return;
      const root = this.$refs?.serviceMultiselect;
      if (root && !root.contains(event.target)) {
        this.service_dropdown_open = false;
      }
    },
    toggle_service_dropdown() {
      if (this.creating_service_binding) return;
      this.service_dropdown_open = !this.service_dropdown_open;
    },
    fetch_services() {
      this.services_loading = true;
      axios.get(GET_VERIFICATION_SERVICES, get_request_config())
        .then((response) => {
          if (response.data?.status !== 'success') {
            this.show_error(response.data.message);
            return;
          }

          this.services = (Array.isArray(response.data.data) ? response.data.data : [])
            .map((service) => ({
              id: service.id,
              name: service.name ||`Service ${service.id}`,
              description: service.description,
            }))
            .filter((service) => service.id);
        })
        .catch(() => {
          this.show_error('Failed to load verification services.');
        })
        .finally(() => {
          this.services_loading = false;
        });
    },

    fetch_service_bindings() {
      const entity_config_id = Number(this.entityConfigId);
      if (!Number.isFinite(entity_config_id) || entity_config_id <= 0) {
        this.service_bindings = [];
        return;
      }

      axios.get(GET_VERIFICATION_SERVICE_BINDINGS,
        get_request_config({ params: { entity_config_id } }))
        .then((response) => {
          if (response.data?.status !== 'success') {
            this.service_bindings = [];
            return;
          }

          this.service_bindings = (Array.isArray(response.data.data) ? response.data.data : [])
            .map((binding) => {
              const service_id = Number(binding.service);
              const matched_service = (Array.isArray(this.services) ? this.services : [])
                .find((service) => String(service?.id) === String(service_id));

              return ({
                id: binding.id,
                service_id,
                name: matched_service?.name || binding.service_name,
                description: matched_service?.description || '',
              });
            })
            .filter((binding) => binding.id);
        })
        .catch(() => {
          this.service_bindings = [];
        });
    },

    normalize_selected_service_ids() {
      return Array.isArray(this.selected_service_ids)
        ? this.selected_service_ids.map((id) => String(id)).filter(Boolean)
        : [];
    },

    async bind_single_service(service_id) {
      const payload = {
        entity_config_id: this.entityConfigId,
        service_id,
      };

      let result;

      try {
        const response = await axios.post(
          BIND_VERIFICATION_SERVICES,
          payload,
          get_request_config()
        );

        if (response.data?.status === 'success') {
          result = { ok: true, service_id };
        } else {
          result = {
            ok: false,
            service_id,
            message: response.data?.message || 'Failed to bind verification service.',
          };
        }
      } catch (e) {
        result = {
          ok: false,
          service_id,
          message: 'Failed to bind verification service.',
        };
      }

      return result;
    },

    async add_service_bindings() {
      const selected_ids = this.normalize_selected_service_ids();

      if (!selected_ids.length) {
        this.show_error('Please select at least one verification service to bind.');
        return;
      }

      if (this.creating_service_binding) return;
      this.creating_service_binding = true;

      try {
        const unique_ids = Array.from(new Set(selected_ids));
        const successes = [];
        const failures = [];

        for (const raw_id of unique_ids) {
          const service_id = Number(raw_id);
          if (!Number.isFinite(service_id) || service_id <= 0) {
            failures.push({ service_id: raw_id, message: 'Invalid service selected.' });
            continue;
          }

          const result = await this.bind_single_service(service_id);
          if (result.ok) {
            successes.push(service_id);
          } else {
            failures.push(result);
          }
        }

        await this.fetch_service_bindings();

        if (successes.length) {
          this.show_success(`${successes.length} service${successes.length === 1 ? '' : 's'} bound successfully.`);
        }

        if (failures.length) {
          this.show_error(
            failures.length === 1
              ? (failures[0]?.message || 'Failed to bind verification service.')
              : `${failures.length} service${failures.length === 1 ? '' : 's'} failed to bind.`
          );
        }

        this.selected_service_ids = failures.map((failure) => String(failure.service_id)).filter(Boolean);
        if (!failures.length) this.service_dropdown_open = false;
      } finally {
        this.creating_service_binding = false;
      }
    },

    get_entity_type_blueprint_name(entityType, index) {
      return entityType?.entity_type_name || entityType?.name || `Entity ${index + 1}`;
    },

    get_entity_type_blueprint_version(entityType, index) {
      return entityType?.version || `VER ${index + 1}`;
    },

    get_entity_type_blueprint_status(entityType) {
      const is_active = entityType?.is_active === true;
      const statuses = {
        status: is_active ? 'Active' : 'Inactive',
        status_class: is_active ? 'success' : 'secondary',
      };
      return statuses
    },

    build_entity_type_blueprint(entityType, index) {
      const subsystem_bindings = entityType?.subsystem_bindings || [];
      const status_data = this.get_entity_type_blueprint_status(entityType);

      const entity_type_data = {
        id: entityType?.id,
        title: this.get_entity_type_blueprint_name(entityType, index),
        version: this.get_entity_type_blueprint_version(entityType, index),
        modified: this.format_relative_date(
          entityType?.date_updated || entityType?.date_created
        ),
        description: entityType?.description || '',
        binding_count: this.bindings_to_count(subsystem_bindings),
        subsystem_bindings,
        status: status_data.status,
        status_class: status_data.status_class,
        disabled: false,
      };

      return entity_type_data;
    },

    remove_service_binding(binding) {
      const binding_id = Number(binding?.id);
      const service_id = Number(binding?.service_id);
      const entity_config_id = Number(this.entityConfigId);

      if (this.removing_binding_id === binding_id) return;

      this.show_confirm(
        'Remove service binding?',
        'This will unbind the verification service from this entity configuration.',
        () => {
          this.removing_binding_id = binding_id;

          const payload = { entity_config_id, service_id };

          axios.post(REMOVE_VERIFICATION_BINDING, payload, get_request_config())
            .then((response) => {
              if (response.data?.status !== 'success') {
                this.show_error(response.data.message);
                return;
              }

              this.service_bindings = this.service_bindings.filter(
                (existing) => Number(existing?.id) !== binding_id
              );
              this.show_success(response.data.message);
            })
            .catch(() => {
              this.show_error('Failed to remove binding.');
            })
            .finally(() => {
              this.removing_binding_id = null;
            });
        }
      );
    },

    go_to_entity_overview() {
      this.$router.push({
        name: 'EntityConfigDetail',
        params: { entityTypeId: this.entityTypeId },
      });
    },
  },
};
